/* eslint-disable import/no-cycle */
import { call, all, put, takeLatest, take, select } from 'redux-saga/effects';
import { getMyRoomsUrl, findUnqiueRoom, roomsUrl, searchRoomsUrl, getMessageUrl } from '../../configurations/api/url';
import {
    setRoomMessagesSuccess,
    setRoomMessagesFail,
    initiateMessageForRoomFail,
    sendMessage,
    sendMessageSuccess,
    searchRoomsSuccess,
    searchRoomsFail,
    updateMessage,
    initiateMessageForRoomSuccess,
} from './actions';
import { getMyRoomsFail, getMyRoomsSuccess } from './actions';
import { types } from './types';
import type { IGetMyRoom, IRoom } from './types';
import { api } from '../../configurations/api';
import { httpRequest } from '../types';
import { globalErrorHandler } from '../error/saga';
import { setInAppNotification } from '../in-app-notification/actions';
import { RootState } from '../root-reducer';
import { uploadMessageFile, uploadMessageFileSuccess, uploadMessageFileFail } from './actions';
import { signedUrlUpload } from '../upload/action';
import { SentryCapture } from '../../analytics/Sentry';
import { types as UplaodTypes } from '../upload/types';
import { ROUTES } from '../../types/global/routes.types';

export const calculateUnreadMessages = (rooms: IRoom[], userdID: string) => {
    let total = 0;
    rooms.forEach(room => {
        if (!room.read_by?.includes(userdID)) {
            total += 1;
        }
    });
    return total;
};

const normalizeRoom = (room: any) => {
    if (!room) return room;
    const normalizedId = room._id || room.id;
    return {
        ...room,
        _id: normalizedId,
        id: normalizedId,
    };
};

const normalizeMessageWithRoom = (message: any, roomId: string) => {
    if (!message) return message;
    return {
        ...message,
        room: typeof message.room === 'string' ? message.room : roomId,
    };
};

function* getMyRooms({
    payload,
    resolve,
    reject,
}: {
    payload: IGetMyRoom;
    resolve: any;
    reject: any;
}): Generator<any, any, any> {
    try {
        const queryParams: Record<string, string> = {};

        if (payload.page !== undefined) queryParams.page = String(payload.page);
        if (payload.limit !== undefined) queryParams.limit = String(payload.limit);

        const query = new URLSearchParams(queryParams).toString();
        const urlWithParams = query ? `${getMyRoomsUrl}?${query}` : getMyRoomsUrl;

        const response = yield call(api, urlWithParams, httpRequest.GET, {}, 0, 0);
        const { data, pagination } = response;

        yield put(getMyRoomsSuccess({ rooms: data, pagination }));
        // yield put(setNotification(calculateUnreadMessages(data, payload)));

        if (resolve) resolve(data);
    } catch (error: any) {
        yield put(getMyRoomsFail(error));
        if (reject) reject(error);
    }
}

/** Just set the room messages without any further calls */
function* getRoomMessages({ payload }: any): any {
    try {
        yield put(setRoomMessagesSuccess(payload));
    } catch (error) {
        yield put(setRoomMessagesFail(error));
    }
}

function* sendRoomMessage({ payload, resolve, reject }: any): any {
    const { sender, receiver, audition, role, message_content, message_type, act, from_modal } = payload;
    try {
        const request = `${findUnqiueRoom}/${receiver}`;
        /** Find if room already exists for this current set of  receivers */
        const response = yield call(api, request, httpRequest.GET, null, 0, 0);
        const { data, results } = response;
        if (results && results > 0 && data) {
            const normalizedExistingRoom = normalizeRoom(data);
            const roomId = normalizedExistingRoom._id;
            const body: any = { room: roomId, message_content, audition, role, message_type, act };
            if (from_modal) {
                const messageResponse = yield call(api, getMessageUrl, httpRequest.POST, body);
                const { data } = messageResponse.data;
                const normalizedRoom = normalizeRoom({
                    ...normalizedExistingRoom,
                    last_message_at: data?.room?.last_message_at || normalizedExistingRoom.last_message_at,
                });
                const normalizedMessage = normalizeMessageWithRoom(data, normalizedRoom._id);
                const load = {
                    ...normalizedRoom,
                    messages: [normalizedMessage, ...(normalizedRoom.messages || [])],
                };
                yield put(
                    sendMessageSuccess({
                        ...normalizedMessage,
                        room: normalizedRoom._id,
                        newRoom: load,
                    }),
                );
                yield put(initiateMessageForRoomSuccess(load));
            } else {
                yield put(sendMessage(body));
            }
            if (resolve) resolve();
        } else {
            // Create a new room;
            const roomBody = { sender, receiver };
            const res = yield call(api, roomsUrl, httpRequest.POST, roomBody, 0, 0);
            if (res) {
                const { data: roomData } = res.data;
                const normalizedCreatedRoom = normalizeRoom(roomData);
                const body: any = { room: normalizedCreatedRoom._id, message_content, audition, role, act };
                if (from_modal) {
                    const messageResponse = yield call(api, getMessageUrl, httpRequest.POST, body);
                    const { data } = messageResponse.data;
                    const normalizedMessage = normalizeMessageWithRoom(data, normalizedCreatedRoom._id);
                    const load = {
                        ...normalizedCreatedRoom,
                        messages: [normalizedMessage, ...(normalizedCreatedRoom.messages || [])],
                    };
                    yield put(
                        sendMessageSuccess({
                            ...normalizedMessage,
                            room: normalizedCreatedRoom._id,
                            newRoom: load,
                        }),
                    );
                    yield put(initiateMessageForRoomSuccess(load));
                } else {
                    yield put(sendMessage(body));
                }
            }
            if (resolve) resolve();
        }
        if (!window.location.pathname.startsWith(ROUTES.MESSAGES)) {
            yield put(
                setInAppNotification({
                    message: 'Message sent successfully',
                    title: 'Message sent successfully',
                }),
            );
        }
    } catch (error: any) {
        console.log(error);
        yield put(initiateMessageForRoomFail(error));
        if (reject) {
            reject(error);
        }
        yield call(globalErrorHandler, error);
    }
}

function* searchRoomsSaga(action: any): Generator<any, any, any> {
    try {
        const { searchTerm, page = 1, limit = 20, searchMessages = false } = action.payload;

        const query = `?searchTerm=${encodeURIComponent(
            searchTerm,
        )}&page=${page}&limit=${limit}&searchMessages=${searchMessages}`;
        const response = yield call(api, `${searchRoomsUrl}${query}`, httpRequest.GET, null, 0, 0);

        if (response.status === 'success') {
            // Format rooms the same way as in retrieveUserRoomsSaga
            const rooms = response.data.rooms.map((item: any) => ({
                ...item.room,
                messages: item.messages,
                matchReason: item.matchReason,
            }));

            yield put(
                searchRoomsSuccess({
                    rooms,
                    pagination: response.data.pagination,
                }),
            );
        } else {
            yield put(searchRoomsFail(response.message));
        }
    } catch (error) {
        yield put(searchRoomsFail(error));
    }
}

// Handle file uploads for messages
function* handleMessageFileUpload(action: {
    type: string;
    payload: { messageId: string; fileItem: { socketId: string; files: File[]; path: string; room: string } };
}): Generator<any, void, any> {
    const { messageId, fileItem } = action.payload;
    const { files, path, socketId, room } = fileItem;
    try {
        // Add safety checks
        if (!fileItem || !fileItem.files) {
            console.error('Invalid file item:', fileItem);
            yield put(
                uploadMessageFileFail({
                    messageId: messageId,
                    error: new Error('Invalid file item format'),
                    socketId,
                }),
            );
            return;
        }

        // Upload the file using the signedUrlUpload saga
        yield put(
            signedUrlUpload({
                id: messageId,
                collection: 'messages',
                directory: 'media',
                path, // 'image' or 'document'
                files,
            }),
        );

        // Wait for the upload result using take instead of takeLatest
        // This ensures we don't miss the upload completion event
        const uploadResult = yield take([UplaodTypes.SIGNED_URL_UPLOAD_SUCCESS, UplaodTypes.SIGNED_URL_UPLOAD_FAIL]);

        if (uploadResult.type === UplaodTypes.SIGNED_URL_UPLOAD_SUCCESS) {
            // If upload succeeded, update the message with the file URLs
            yield put(
                updateMessage({
                    messageId: messageId,
                    room: room,
                    [path]: uploadResult.payload,
                }),
            );
            yield put(uploadMessageFileSuccess({ messageId, fileUrls: uploadResult.payload, socketId }));
        } else {
            // If upload failed, handle the error
            /** Dispatch an action to delete the image from the message for everyone */
            yield put(uploadMessageFileFail({ messageId, error: uploadResult.payload, socketId }));
        }
    } catch (error) {
        SentryCapture(error);
        yield put(uploadMessageFileFail({ messageId: action.payload.messageId, error, socketId: socketId }));
    }
}

// Watch for successful message sends with pending file uploads
function* watchMessagesSentWithFiles(): Generator<any, void, any> {
    yield takeLatest(types.SEND_MESSAGE_SUCCESS, function* (action: any): Generator<any, void, any> {
        try {
            // Skip processing if it's just a normal chat message
            if (action.payload.message_type === 'chat') {
                return;
            }

            // Get the queue of pending file uploads from the state
            const state: RootState = yield select();
            const fileUploadQueue = state.messages.fileUploadQueue || [];

            // Ensure fileUploadQueue is an array
            if (!Array.isArray(fileUploadQueue)) {
                console.warn('fileUploadQueue is not an array:', fileUploadQueue);
                return;
            }

            // Get the messageId and socketId from the successful message
            const messageId = action.payload._id;
            const socketId = action.payload.uniqueSocketID;

            // Find all uploads that match this socketId (there could be multiple files)
            const queuedItems = fileUploadQueue.filter((item: { socketId: string }) => item.socketId === socketId);

            if (queuedItems.length > 0) {
                // Process each queued item - now that we have the real messageId
                for (const fileItem of queuedItems) {
                    // Update the fileItem with the correct messageId
                    const updatedFileItem = {
                        messageId,
                        ...fileItem,
                    };

                    // Dispatch the upload action with the updated item
                    yield put(
                        uploadMessageFile({
                            messageId,
                            fileItem: updatedFileItem as any,
                            socketId: updatedFileItem.socketId,
                            room: fileItem.room,
                        }),
                    );
                }
            }
        } catch (error) {
            console.error('Error processing message file uploads:', error);
        }
    });
}

// Add a watcher for file upload actions
function* uploadMessageFileWatcher() {
    yield takeLatest(types.UPLOAD_MESSAGE_FILE, handleMessageFileUpload);
}

function* getMyRoomsWatcher() {
    yield takeLatest<any>(types.GET_MY_ROOMS, getMyRooms);
}

function* getRoomMesssagesWatcher() {
    yield takeLatest(types.SET_ROOM_MESSAGES, getRoomMessages);
}

function* sendMessageWatcher() {
    yield takeLatest(types.INITIATE_MESSAGE, sendRoomMessage);
}

function* searchRoomsWatcher() {
    yield takeLatest(types.SEARCH_ROOMS, searchRoomsSaga);
}

export default function* messageSaga() {
    yield all([
        getMyRoomsWatcher(),
        getRoomMesssagesWatcher(),
        sendMessageWatcher(),
        searchRoomsWatcher(),
        uploadMessageFileWatcher(),
        watchMessagesSentWithFiles(),
    ]);
}
